/**
 * The session. Config, auth client, API client and principal in one hook, so a
 * screen never wires Firebase itself.
 */

import { createContext, createElement, useContext, useEffect, useMemo, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import type { Role } from "@ksidc/ontology";
import { createAuthClient, type AuthClient, type PublicConfig, type SessionUser } from "./auth.js";
import { createApiClient, type ApiClient } from "./api.js";

/**
 * The roles, from the one place that defines them.
 *
 * This used to be its own copy of the list and had already fallen behind: it
 * was missing the Minister, so the console could not compare against a role the
 * API was handing it. A vocabulary with two definitions has one definition and
 * one bug.
 */
export type { Role } from "@ksidc/ontology";
export { ROLES, isRole, ROLE_LABELS } from "@ksidc/ontology";

export type ScopeKind = "none" | "office" | "authority" | "board" | "district" | "state";

export interface Scope {
  kind: ScopeKind;
  id: string | null;
}

export interface Principal {
  uid: string;
  email: string | null;
  displayName: string | null;
  role: Role;
  boardId: string | null;
  /** What this person may act over. Role says what they may do. */
  scope: Scope;
  lang: "en" | "ml";
}

export interface SessionValue {
  status: "loading" | "signed-out" | "signed-in" | "error";
  config: PublicConfig | null;
  auth: AuthClient | null;
  api: ApiClient;
  user: SessionUser | null;
  principal: Principal | null;
  error: string | null;
  reload: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): ReactElement {
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [auth, setAuth] = useState<AuthClient | null>(null);
  const [user, setUser] = useState<SessionUser | null>(null);
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [status, setStatus] = useState<SessionValue["status"]>("loading");
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const api = useMemo(() => createApiClient(auth), [auth]);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const res = await fetch("/api/config");
        if (!res.ok) throw new Error(`config ${res.status}`);
        const cfg = (await res.json()) as PublicConfig;
        if (!live) return;
        setConfig(cfg);
        setAuth(createAuthClient(cfg));
      } catch (e) {
        if (!live) return;
        setError(e instanceof Error ? e.message : "could not load configuration");
        setStatus("error");
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (!auth) return;
    return auth.subscribe((u) => {
      setUser(u);
      if (!u) {
        setPrincipal(null);
        setStatus("signed-out");
      }
    });
  }, [auth]);

  useEffect(() => {
    if (!user || !auth) return;
    let live = true;
    void (async () => {
      try {
        const me = await createApiClient(auth).get<{ principal: Principal }>("/me");
        if (!live) return;
        setPrincipal(me.principal);
        setStatus("signed-in");
      } catch (e) {
        if (!live) return;
        setError(e instanceof Error ? e.message : "could not load your account");
        setStatus("error");
      }
    })();
    return () => {
      live = false;
    };
  }, [user, auth, nonce]);

  const value: SessionValue = {
    status,
    config,
    auth,
    api,
    user,
    principal,
    error,
    reload: () => setNonce((n) => n + 1),
  };

  return createElement(SessionContext.Provider, { value }, children);
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside SessionProvider");
  return ctx;
}
